import { FC, memo, useEffect } from 'react';
import {
  Modal, Form, Input, Button, Space,
} from 'antd';
import { UserDetailResponseDto, UserRequestDto } from 'shared/api';
import styles from './EditProfileModal.module.scss';

interface EditProfileModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: UserRequestDto) => void;
  user: UserDetailResponseDto | null;
}

export const EditProfileModal: FC<EditProfileModalProps> = memo(({
  open,
  onClose,
  onSave,
  user,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({
        firstname: user.firstname,
        lastname: user.lastname,
        middleName: user.middleName,
        email: user.email,
        mobilePhone: user.mobilePhone,
        company: user.company,
        status: user.status,
        innCompany: user.innCompany,
      });
    }
  }, [open, user, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      onSave(values);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Редактирование профиля"
      open={open}
      onCancel={handleCancel}
      width={600}
      className={styles.modal}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Отмена
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          Сохранить
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        className={styles.form}
      >
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Личная информация</h3>
          <Space.Compact direction="vertical" className={styles.fieldGroup}>
            <Form.Item
              name="firstname"
              label="Имя"
              rules={[
                { required: true, message: 'Пожалуйста, введите имя' },
                { min: 2, message: 'Имя должно содержать минимум 2 символа' },
              ]}
            >
              <Input placeholder="Введите имя" />
            </Form.Item>

            <Form.Item
              name="lastname"
              label="Фамилия"
              rules={[
                { required: true, message: 'Пожалуйста, введите фамилию' },
                { min: 2, message: 'Фамилия должна содержать минимум 2 символа' },
              ]}
            >
              <Input placeholder="Введите фамилию" />
            </Form.Item>

            <Form.Item
              name="middleName"
              label="Отчество"
            >
              <Input placeholder="Введите отчество" />
            </Form.Item>
          </Space.Compact>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Контактная информация</h3>
          <Space.Compact direction="vertical" className={styles.fieldGroup}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Пожалуйста, введите email' },
                { type: 'email', message: 'Введите корректный email' },
              ]}
            >
              <Input placeholder="Введите email" />
            </Form.Item>

            <Form.Item
              name="mobilePhone"
              label="Телефон"
              rules={[
                { max: 32, message: 'Не более 32 символов' },
                {
                  pattern: /^$|^[+()\d\s-]+$/,
                  message: 'Допустимы цифры, пробелы и символы + ( ) -',
                },
              ]}
            >
              <Input placeholder="+7 (999) 123-45-67" autoComplete="tel" />
            </Form.Item>
          </Space.Compact>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Дополнительная информация</h3>
          <Space.Compact direction="vertical" className={styles.fieldGroup}>
            <Form.Item
              name="company"
              label="Компания"
            >
              <Input placeholder="Введите название компании" />
            </Form.Item>

            <Form.Item
              name="innCompany"
              label="ИНН компании"
              rules={[
                { min: 10, message: 'ИНН компании должен содержать минимум 10 символов' },
                { max: 12, message: 'ИНН компании может содержать максимум 12 символов' },
                { pattern: /^[0-9]+$/, message: 'ИНН компании должен содержать только цифры' },
              ]}
            >
              <Input placeholder="Введите ИНН компании" />
            </Form.Item>
          </Space.Compact>
        </div>
      </Form>
    </Modal>
  );
});

EditProfileModal.displayName = 'EditProfileModal';
